import {Injectable, inject, signal} from "@angular/core"
import {LayerSelection} from "@template-editor/helpers/layer-jobs"
import {JobData, UnassignedJobData} from "@template-editor/helpers/template-jobs"
import {LayerPlan} from "@cm/template-nodes"
import {DEFAULT_RENDER_QUEUE_GROUP} from "@cm/job-nodes/rendering"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {CM_LOGGER_TEMPLATES} from "@common/services/logging"
import {GetRenderQueueGroupsForTemplateImageViewerGQL} from "@template-editor/components/template-image-viewer/template-image-viewer.component.generated"
import {Subject} from "rxjs"

@Injectable()
export class TemplateJobManagerService {
    private readonly renderQueueGroupsGql = inject(GetRenderQueueGroupsForTemplateImageViewerGQL)
    private readonly logger = inject(CM_LOGGER_TEMPLATES)

    readonly $unassignedJobs = signal<UnassignedJobData[]>([])
    readonly $selectedUnassignedJob = signal<UnassignedJobData | undefined>(undefined)
    // Emits after jobs were discarded elsewhere, so a view showing one of them reloads.
    readonly jobsDiscarded$ = new Subject<void>()

    readonly $layerMode = signal(false)
    readonly $layerPlan = signal<LayerPlan | undefined>(undefined)
    readonly $layerJobs = signal<ReadonlyMap<string, JobData>>(new Map())
    readonly $soloLayer = signal<LayerSelection | undefined>(undefined)

    readonly $renderQueueGroups = signal<string[]>([])
    // mat-select treats a null option value as "nothing selected" and clears itself, so cloud rendering
    // is the empty string rather than null
    readonly $renderQueueGroup = signal<string>("")
    private renderQueueGroupsRequested = false

    async loadRenderQueueGroups() {
        if (this.renderQueueGroupsRequested) return
        this.renderQueueGroupsRequested = true
        try {
            const {jobTaskTypes} = await fetchThrowingErrors(this.renderQueueGroupsGql)({filter: {taskType: {equals: "Rendering.cmRender"}}})
            // the default is already offered as "Cloud rendering", and selecting it would route identically
            const queueGroups = Array.from(
                new Set(jobTaskTypes.flatMap((taskType) => (taskType && taskType.queueGroup !== DEFAULT_RENDER_QUEUE_GROUP ? [taskType.queueGroup] : []))),
            ).sort()
            this.$renderQueueGroups.set(queueGroups)
        } catch (error) {
            this.renderQueueGroupsRequested = false
            this.logger.warn("Could not load the render queue groups", error)
        }
    }
}
